import type { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { FunctionComponent } from 'react';

import {
    TableBody,
    TableCell,
    Table as TableComponent,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableSkeleton,
} from '~/ui/core/table/Table';

import type { UploadedDocument } from './types';

type DocumentsTableProps = {
    readonly isLoading: boolean;
    readonly table: Table<UploadedDocument>;
};

const FILE_COL_WIDTHS = ['40px', '1fr', '120px', '180px', '80px'];

export const DocumentsTable: FunctionComponent<DocumentsTableProps> = ({ isLoading, table }) => {
    if (isLoading) {
        return <TableSkeleton aria-label="Loading documents" columnWidths={FILE_COL_WIDTHS} rowCount={3} />;
    }

    if (table.getRowModel().rows.length === 0) {
        return null;
    }

    return (
        <TableComponent aria-label="Uploaded files" style={{ height: 'auto' }}>
            <TableHeader $columnWidths={FILE_COL_WIDTHS}>
                {table
                    .getHeaderGroups()
                    .map((headerGroup) =>
                        headerGroup.headers.map((header) => (
                            <TableHeaderCell key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHeaderCell>
                        )),
                    )}
            </TableHeader>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} $columnWidths={FILE_COL_WIDTHS}>
                        {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </TableComponent>
    );
};
